/**
 * Client-side supervisory-subtree helpers for the Team page.
 *
 * These exist ONLY to stop the UI offering a supervisor/transfer choice the server will
 * reject with a 400 (#505). They are cosmetic: the backend cycle guard in
 * `MembersService` is authoritative, and it stays authoritative because the lists these
 * walk are paginated and filtered to active members, so an inactive mid-chain supervisor
 * can hide part of a subtree from the client but never from the server.
 */

/** The minimum shape either walk needs — both `HierarchyMember` and `Member` satisfy it. */
export interface SupervisedNode {
  id: string;
  supervisorId?: string | null;
}

/** Adjacency: supervisor id -> direct subordinate ids. */
function buildChildrenMap(members: SupervisedNode[]): Map<string, string[]> {
  const childrenBySupervisor = new Map<string, string[]>();
  for (const m of members) {
    if (!m.supervisorId) continue;
    const bucket = childrenBySupervisor.get(m.supervisorId);
    if (bucket) bucket.push(m.id);
    else childrenBySupervisor.set(m.supervisorId, [m.id]);
  }
  return childrenBySupervisor;
}

/**
 * BFS down from one root over a prebuilt adjacency map.
 *
 * The `visited` guard keeps the walk terminating on hierarchies that are ALREADY cyclic,
 * which is exactly the data this feature exists to stop being created. Both exported
 * helpers share this one body so that guard can never drift between them.
 */
function walkDescendants(
  childrenBySupervisor: Map<string, string[]>,
  rootId: string,
): Set<string> {
  const descendants = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const childId of childrenBySupervisor.get(current) ?? []) {
      if (childId === rootId || descendants.has(childId)) continue;
      descendants.add(childId);
      queue.push(childId);
    }
  }
  return descendants;
}

/** Every member beneath `rootId` — direct and indirect subordinates, excluding the root. */
export function getDescendantIds(members: SupervisedNode[], rootId: string): Set<string> {
  return walkDescendants(buildChildrenMap(members), rootId);
}

/**
 * {@link getDescendantIds} for every member at once, sharing one adjacency map. Used by
 * the Hierarchy tab, which needs the answer for each row it renders.
 */
export function buildSubtreeIndex(members: SupervisedNode[]): Map<string, Set<string>> {
  const childrenBySupervisor = buildChildrenMap(members);
  return new Map(members.map((m) => [m.id, walkDescendants(childrenBySupervisor, m.id)]));
}
